import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import qs from "qs";
import {
  GetTasksApiV1TasksGetQueryParams,
  getTasksApiV1TasksGetQuery,
} from "vl-api/vlComponents";
import { PaginatedTasksResponse } from "vl-api/vlSchemas";

const baseUrl = String(process.env.REACT_APP_API_ENDPOINT);

/**
 * Long-polls GET /api/v1/tasks with wait/etag params and pushes updates
 * into the React Query cache so components using useGetTasksApiV1TasksGet
 * re-render automatically.
 */
export function useTasksLongPoll(
  queryParams: GetTasksApiV1TasksGetQueryParams,
) {
  const queryClient = useQueryClient();
  const etagRef = useRef<string | null>(null);

  useEffect(() => {
    const abortController = new AbortController();
    let cancelled = false;

    (async () => {
      let retryDelay = 5000;
      while (!cancelled) {
        try {
          const params = {
            ...queryParams,
            wait: "true",
            ...(etagRef.current ? { etag: etagRef.current } : {}),
          };
          const query = qs.stringify(params);
          const response = await fetch(`${baseUrl}/api/v1/tasks?${query}`, {
            credentials: "include",
            signal: abortController.signal,
          });

          if (cancelled) break;

          if (response.status === 304) {
            retryDelay = 5000;
            continue;
          }

          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }

          etagRef.current = response.headers.get("x-etag");
          const data: PaginatedTasksResponse = await response.json();

          // Update the React Query cache for the same query key
          const { queryKey } = getTasksApiV1TasksGetQuery({
            queryParams,
          });
          queryClient.setQueryData(queryKey, data);

          retryDelay = 5000;
        } catch {
          if (cancelled) break;
          const delay = retryDelay;
          await new Promise((r) => setTimeout(r, delay));
          retryDelay = Math.min(retryDelay * 2, 30000);
        }
      }
    })();

    return () => {
      cancelled = true;
      abortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, JSON.stringify(queryParams)]);
}
